// @flow
import React from 'react';

import Box from '@waglabs/box';
import H1 from '@waglabs/h1';
import H2 from '@waglabs/h2';
import Span from '@waglabs/span';
import Text from '@waglabs/text';

import { BodyContainer } from '../components/BodyContainer';
import { Layout } from '../components/Layout';

type Props = {
  location: {
    pathname: string,
    href: string,
  },
};

const TERMS_FONT_COLOR = 'medGray';
const TERMS_FONT_SIZE = '15px';

const Terms = ({
  location: {
    pathname,
    href,
  },
}: Props) => (
  <Layout
    currentPathname={pathname}
    currentUrl={href}
    disableAppInstall
    disableNavbar
    disableFooter
  >
    <BodyContainer
      alignItems="flex-start"
      mt={3}
      px={3}
    >
      <H1
        color={TERMS_FONT_COLOR}
        fontWeight="bold"
      >
        Wag! Tag Terms of Service
      </H1>

      <Text
        color={TERMS_FONT_COLOR}
        fontSize={TERMS_FONT_SIZE}
      >
        Updated: July 25, 2019
      </Text>

      <Text
        color={TERMS_FONT_COLOR}
        fontSize={TERMS_FONT_SIZE}
      >
        {'These Wag! Tag Terms of Service (the '}
        <Span
          fontWeight="bold"
        >
          &quot;Wag! Tag Terms&quot;
        </Span>
        ) govern your use of the Wag! Tag
        {' and the subscription services provided in connection therewith (the '}
        <Span
          fontWeight="bold"
        >
          &quot;Wag! Tag Services&quot;
        </Span>
        {') as further described herein and are subject to and incorporate the Terms of Service located'}
        <a
          href="https://wagwalking.com/terms?plain=1"
          rel="noopener noreferrer"
          target="_blank"
        >
          {' here'}
        </a>
        {' ('}
        <Span
          fontWeight="bold"
        >
          &quot;Terms of Service&quot;
        </Span>
        ),
        <Span
          fontWeight="bold"
        >
          {' including the mutual arbitration agreement and class action waiver that requires you and Wag! to resolve disputes with each other on an individual basis through final and binding arbitration. '}
        </Span>
        Wag! Labs, Inc. (
        <Span
          fontWeight="bold"
        >
          &quot;Wag!&quot;
        </Span>
        {', '}
        <Span
          fontWeight="bold"
        >
        &quot;Company&quot;
        </Span>
        {', '}
        <Span
          fontWeight="bold"
        >
          &quot;we&quot;
        </Span>
        {' or '}
        <Span
          fontWeight="bold"
        >
          &quot;us&quot;
        </Span>
        {') provides the Wag! Tag Services. '}
        <Span
          fontWeight="bold"
        >
          &quot;You&quot;
        </Span>
        {' refers to you as a user of the Wag! Tag Services. The Wag! Tag Services constitute "Services" under the Terms of Service. Any capitalized terms used in these Wag! Tag Terms that are not defined elsewhere in these Wag! Tag Terms shall have the definitions assigned to them in the Terms of Service.'}
      </Text>

      <Text
        color={TERMS_FONT_COLOR}
        fontSize={TERMS_FONT_SIZE}
      >
        {'The Wag! Tag Services includes the animal tag with a unique ID code and QR code ('}
        <Span
          fontWeight="bold"
        >
          &quot;Wag! Tag&quot;
        </Span>
        {') that, when scanned, identifies the pet and retrieves your contact information, which is used to contact you and reunite you with your pet.'}
      </Text>

      <Box>
        <H2
          color={TERMS_FONT_COLOR}
          fontSize={5}
          fontWeight="bold"
        >
          1. Acceptance of these Wag! Tag Terms
        </H2>

        <Text
          color={TERMS_FONT_COLOR}
          fontSize={TERMS_FONT_SIZE}
        >
          {'By using the Wag Tag! Services, you expressly acknowledge that you have read, understand and agree to these Wag! Tag Terms. IF YOU DO NOT AGREE TO BE BOUND BY THE TERMS AND CONDITIONS OF THESE WAG! TAG TERMS, YOU MAY NOT USE OR ACCESS THE WAG! TAG SERVICES.'}
        </Text>
      </Box>

      <Box>
        <H2
          color={TERMS_FONT_COLOR}
          fontSize={5}
          fontWeight="bold"
        >
          2. Changes to these Wag! Tag Terms
        </H2>

        <Text
          color={TERMS_FONT_COLOR}
          fontSize={TERMS_FONT_SIZE}
        >
          {'Wag! may, in its sole discretion, amend these Wag! Tag Terms from time to time. Please check this page for any changes. The date of the last update can be found at the top of this page. If we make any changes to these Wag! Tag Terms, your continued use of the Wag! Tag Services after the date set forth above will constitute your agreement to the amended Wag! Tag Terms.'}
        </Text>
      </Box>

      <Box>
        <H2
          color={TERMS_FONT_COLOR}
          fontSize={5}
          fontWeight="bold"
        >
          3. Creating an Account
        </H2>
        <Text
          color={TERMS_FONT_COLOR}
          fontSize={TERMS_FONT_SIZE}
        >
          {'In order to use the Wag! Tag Services, you must have an account with us. By creating an account, you represent and warrant that (a) all information you provide us is truthful and accurate and will not pose any danger to anyone or any pet; (b) you are of legal age to agree to these Wag! Tag Terms; (c) your use of the Wag! Tag Services does not violate any applicable laws; (d) you are the legal owner of the pets for which the Wag! Tag Services are being used; and (e) you will only use the Wag! Tag Services for your dog, and no other domesticated or exotic pet. You are responsible for maintaining the confidentiality of your password and for any and all use of your account.'}
        </Text>
      </Box>

      <Box>
        <H2
          color={TERMS_FONT_COLOR}
          fontSize={5}
          fontWeight="bold"
        >
          4. Payment
        </H2>
        <Text
          color={TERMS_FONT_COLOR}
          fontSize={TERMS_FONT_SIZE}
        >
          {'You understand that your use of the Wag! Tag Services may result in you owing fees to Wag!. Charges will be inclusive of applicable taxes where required by law.  Fees paid by you are final and non-refundable regardless of your decision to terminate usage of the Wag! Tag Services, Wag!’s decision to terminate your usage, any disruption of the Wag! Tag Services or any other reason whatsoever, unless otherwise determined by Wag!. All charges are due immediately. If your primary payment method is determined to be expired, invalid or otherwise not able to be charged, you agree that Wag! may use a secondary payment method in your account, if available.'}
        </Text>

        <Text
          color={TERMS_FONT_COLOR}
          fontSize={TERMS_FONT_SIZE}
        >
          {'You understand that if you have signed up for a subscription to your Wag! Tag Service, your subscription is automatically renewed for an additional period equal to the length of your subscription period, unless you cancel your subscription. For example, a monthly subscription will automatically renew for additional one-month periods until you cancel it; likewise, a yearly subscription will automatically renew for additional one-year periods until you cancel it. Such automatic renewal is continuous, and you will be billed for each continuous subscription period at the rates in effect when your subscription is renewed. Subscriptions must be cancelled before the start of the new subscription period to not incur another subscription period’s charges.'}
        </Text>
      </Box>

      <Box>
        <H2
          color={TERMS_FONT_COLOR}
          fontSize={5}
          fontWeight="bold"
        >
          {'5. Your Consent to Make Your Information Available and to Receive Calls, Text Messages, Emails and Other Communications'}
        </H2>
        <Text
          color={TERMS_FONT_COLOR}
          fontSize={TERMS_FONT_SIZE}
        >
          {'As part of the Wag! Tag Services and as described in the Terms of Service, you may receive push notifications, text messages, alerts, emails or other types of messages sent directly to you at the e-mail addresses and/or phone numbers provided by you. You understand that we may use automated technology to send such messages, and such communications may include promotional content. You also understand that, in addition to these notifications, we may use the e-mail addresses and/or phone numbers you provided to contact you if your lost pet is found. You acknowledge that any messages we send you may be subject to related carrier charges, and you agree to such charges.'}
        </Text>

        <Text
          color={TERMS_FONT_COLOR}
          fontSize={TERMS_FONT_SIZE}
        >
          {'To provide the Wag! Tag Services, your Wag! Tag may be associated with your e-mail address, phone number, and/or other information (including any User Content) you provide in your Wag! Tag account, such as health information of your pet and any third-party emergency contact information. Third parties who scan the Wag! Tag may thus have access to such personal information of yours, your pet and/or your emergency contacts and may call you and/or your emergency contacts or otherwise communicate with you and/or your emergency contacts (e.g., to inform you and/or your emergency contacts of the location of your pet). You expressly agree that we may make such information available through the Wag! Tag and Wag! Tag Services, and you acknowledge and agree that Wag! cannot control nor is responsible for the conduct of third parties who interact with you and/or your emergency contacts or obtain the personal information of yours, your pet and/or your emergency contacts through your Wag! Tag or the Wag! Tag Services. You are advised to take reasonable precautions with respect to interactions with third parties encountered in connection with the Wag! Tag Services. When you provide an emergency contact’s information in your Wag! Tag account, you represent and warrant that you are authorized to (a) provide such information; (b) approve the receipt of calls and text messages to the telephone number provided to Wag! by you; and (c) approve any related carrier charges.'}
        </Text>
      </Box>

      <Box>
        <H2
          color={TERMS_FONT_COLOR}
          fontSize={5}
          fontWeight="bold"
        >
          6. Rewards
        </H2>
        <Text
          color={TERMS_FONT_COLOR}
          fontSize={TERMS_FONT_SIZE}
        >
          {'You may, in your sole discretion, offer a reward to a third party who may find and return your pet. In the event that you decide to provide a reward, you will handle any payments of that reward directly with the third party. Wag! shall have no responsibility or liability in connection with any such reward.'}
        </Text>
      </Box>

      <Box>
        <H2
          color={TERMS_FONT_COLOR}
          fontSize={5}
          fontWeight="bold"
        >
          7. Disclaimers and Limitations on Our Liability
        </H2>
        <Text
          color={TERMS_FONT_COLOR}
          fontSize={TERMS_FONT_SIZE}
        >
          {'YOU USE THE WAG! TAG SERVICES AT YOUR OWN RISK. THE WAG! TAG SERVICES ARE PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS. TO THE EXTENT PERMITTED BY APPLICABLE LAW, WAG! AND ITS OFFICERS, EMPLOYEES, DIRECTORS, SHAREHOLDERS, PARENTS, SUBSIDIARIES, AFFILIATES, AGENTS, AND LICENSORS ('}
          <Span
            fontWeight="bold"
          >
            “AFFILIATES”
          </Span>
          {') DISCLAIM ALL WARRANTIES, CONDITIONS, AND REPRESENTATIONS OF ANY KIND, WHETHER EXPRESS, IMPLIED, STATUTORY, OR OTHERWISE, INCLUDING THOSE RELATED TO MERCHANTABILITY, SATISFACTORY QUALITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT AND THOSE ARISING OUT OF COURSE OF DEALING OR USAGE OF TRADE. WAG! MAKES NO WARRANTIES THAT YOUR USE OF THE WAG! TAG SERVICES WILL RESULT IN YOUR REUNITING WITH YOUR LOST PET.'}
        </Text>

        <Text
          color={TERMS_FONT_COLOR}
          fontSize={TERMS_FONT_SIZE}
        >
          {'YOU ACKNOWLEDGE THAT USING THE WAG! TAG SERVICES DOES NOT MEAN THAT: (A) YOUR PET WILL NOT BE LOST OR STOLEN OR (B) YOUR PET WILL BE RETURNED TO YOU IF IT IS LOST OR STOLEN. YOU ACKNOWLEDGE THAT WAG! DOES NOT GUARANTEE THAT ANY LOST PET WILL BE FOUND AND THAT WAG! DOES NOT EMPLOY ANY SERVICE FOR THE PURPOSE OF SEARCHING FOR LOST PETS. WAG! WILL NOT TAKE AN ACTIVE ROLE IN SEARCHING FOR ANY PET. WAG!’S ONLY RESPONSIBILITY IS TO MAINTAIN A DATABASE OF YOUR CONTACT AND PET INFORMATION IN THE EVENT YOUR PET IS FOUND. IF YOUR PET IS NOT RETURNED TO YOU, YOU WILL NOT BE ENTITLED TO A REFUND OR ANY REIMBURSEMENT.'}
        </Text>

        <Text
          color={TERMS_FONT_COLOR}
          fontSize={TERMS_FONT_SIZE}
        >
          {'YOU ALSO ACKNOWLEDGE THAT YOU MAY COLLECT YOUR LOST PET FROM A THIRD PARTY THAT IS UNKNOWN TO YOU. WE URGE YOU TO TAKE PRECAUTIONS WHEN MEETING WITH A THIRD PARTY TO RETRIEVE YOUR PET (SUCH AS MEETING IN A PUBLIC LOCATION). YOU ACKNOWLEDGE AND AGREE THAT ALL SUCH MEETINGS ARE AT YOUR OWN RISK AND WAG! SHALL HAVE NO LIABILITY OR RESPONSIBILITY ARISING FROM SUCH MEETING, INCLUDING FOR ANY CRIME COMMITTED BY OR AGAINST YOU BY A THIRD PARTY, ANY COMMUNICATION BETWEEN YOU AND A THIRD PARTY OR IDENTITY THEFT RESULTING FROM A LOST PET OR USE OF THE WAG! TAG SERVICES.'}
        </Text>
      </Box>

      <Box>
        <H2
          color={TERMS_FONT_COLOR}
          fontSize={5}
          fontWeight="bold"
        >
          8. Indemnity
        </H2>
        <Text
          color={TERMS_FONT_COLOR}
          fontSize={TERMS_FONT_SIZE}
        >
          {'You agree to indemnify Wag! and its Affiliates from and against all claims, costs, proceedings, demands, losses, damages and expenses (including, without limitation, reasonable attorney\'s fees and legal costs) of any kind or nature, arising from or relating to any meetings between you and a third party that is facilitated through the Wag! Tag Services.'}
        </Text>
      </Box>

      <Box>
        <H2
          color={TERMS_FONT_COLOR}
          fontSize={5}
          fontWeight="bold"
        >
          9. Terms of Service
        </H2>
        <Text
          color={TERMS_FONT_COLOR}
          fontSize={TERMS_FONT_SIZE}
        >
          {'Other than as specifically stated in these Wag! Tag Terms, the use of the Wag! Tag Services is subject to the Terms of Service, and the Terms of Service shall apply to your use of the Wag! Tag Services. In the event that any of the provisions in the Company\'s Terms of Service or Privacy Policy conflict with the provisions of these Wag! Tag Terms, these Wag! Tag Terms shall control unless these Wag! Tag Terms expressly provide otherwise.'}
        </Text>
      </Box>

    </BodyContainer>
  </Layout>
);

export default Terms;
